/**
 * PrintForge Labs — Custom Theme JS
 * Mobile menu, scroll reveals, cart interactions, UI enhancements
 * No framework dependencies — plain modern JS
 */

(function () {
  'use strict';

  /* ── Scroll Reveal ──────────────────────────────────────── */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          // Stagger delay for siblings
          setTimeout(() => {
            entry.target.classList.add('is-visible');
          }, i * 80);
          revealObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );

  function initReveal() {
    document.querySelectorAll('.pf-reveal').forEach((el) => {
      revealObserver.observe(el);
    });
  }

  /* ── Visual Drop Timer (display-only) ─────────────────── */
  function initDropTimers() {
    document.querySelectorAll('[data-pf-timer]').forEach((wrap) => {
      // Replace placeholder dashes with animated fake values
      // In a real implementation, wire to a real end date
      const blocks = wrap.querySelectorAll('.pf-drop__timer-num');
      const placeholders = ['08', '23', '47', '12'];
      blocks.forEach((b, i) => {
        if (b.textContent.trim() === '--' && placeholders[i]) {
          b.textContent = placeholders[i];
        }
      });
    });
  }

  /* initHeaderScroll intentionally removed — Dawn's StickyHeader custom element
     already implements hide-on-scroll with its own scroll listener and CSS class
     transitions. A second listener writing inline style.transform would conflict. */

  /* ── Product card: pointer-tracking glow on hover ──────── */
  function initCardGlow() {
    // Only attach on pointer:fine devices (mouse/trackpad). On touch-only devices
    // mousemove never fires and per-card listeners would be dead weight.
    if (!window.matchMedia('(pointer: fine)').matches) return;

    document.querySelectorAll('.pf-product-card, .pf-collection-card').forEach((card) => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        card.style.setProperty('--mouse-x', `${x}%`);
        card.style.setProperty('--mouse-y', `${y}%`);
      });
      card.addEventListener('mouseleave', () => {
        card.style.removeProperty('--mouse-x');
        card.style.removeProperty('--mouse-y');
      });
    });
  }

  /* ── Accordion: product page details ───────────────────── */
  function initAccordions() {
    document.querySelectorAll('.product__accordion details').forEach((det) => {
      det.addEventListener('toggle', () => {
        if (det.open) {
          // Close siblings
          det.closest('.product__accordion')
            ?.querySelectorAll('details[open]')
            .forEach((other) => {
              if (other !== det) other.open = false;
            });
        }
      });
    });
  }

  /* ── Custom Keychain Form: Forminit SDK submission + image preview ── */
  function initKeychainForms() {
    // Wire upload zones (drag-drop + click + preview)
    document.querySelectorAll('.pf-keychain-file-input').forEach((input) => {
      const zone = document.getElementById(input.dataset.zone);
      if (!zone) return;

      const placeholder = zone.querySelector('.pf-upload-zone__placeholder');
      const preview = zone.querySelector('.pf-upload-zone__preview');
      const removeBtn = zone.querySelector('.pf-upload-zone__remove');
      let objectUrl = null;

      zone.addEventListener('click', (e) => {
        if (removeBtn && (e.target === removeBtn || removeBtn.contains(e.target))) return;
        input.click();
      });
      zone.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); }
      });
      zone.addEventListener('dragover', (e) => {
        e.preventDefault();
        zone.classList.add('is-dragover');
      });
      zone.addEventListener('dragleave', () => zone.classList.remove('is-dragover'));
      zone.addEventListener('drop', (e) => {
        e.preventDefault();
        zone.classList.remove('is-dragover');
        const file = e.dataTransfer.files[0];
        if (file) setFile(file);
      });
      input.addEventListener('change', () => {
        if (input.files[0]) setFile(input.files[0]);
      });
      if (removeBtn) {
        removeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          clearUpload();
        });
      }

      function setFile(file) {
        if (!file.type.startsWith('image/')) return;
        if (file.size > 10 * 1024 * 1024) {
          alert('Please upload an image under 10 MB.');
          return;
        }
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        objectUrl = URL.createObjectURL(file);
        // Store on element so submit handler can append drag-dropped files to FormData
        // (input.files is read-only; drag-and-drop files never populate it)
        input._selectedFile = file;
        preview.src = objectUrl;
        preview.style.display = 'block';
        if (placeholder) placeholder.style.display = 'none';
        if (removeBtn) removeBtn.style.display = 'flex';
        zone.classList.add('has-file');
      }

      function clearUpload() {
        if (objectUrl) { URL.revokeObjectURL(objectUrl); objectUrl = null; }
        input._selectedFile = null;
        preview.src = '';
        preview.style.display = 'none';
        if (placeholder) placeholder.style.display = 'flex';
        if (removeBtn) removeBtn.style.display = 'none';
        zone.classList.remove('has-file');
        input.value = '';
      }
    });

    document.querySelectorAll('.pf-keychain-forminit').forEach((form) => {
      const formId    = form.dataset.formId;
      const sectionId = form.dataset.section;
      const btnLabel  = form.dataset.btnLabel || 'Send My Request';
      const successEl = document.getElementById('pf-keychain-success-' + sectionId);
      const errorsEl  = document.getElementById('pf-keychain-errors-' + sectionId);
      const submitBtn = form.querySelector('.pf-keychain-submit');
      const btnSvg    = submitBtn ? submitBtn.querySelector('svg') : null;

      if (!formId) return;

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!form.checkValidity()) { form.reportValidity(); return; }

        submitBtn.disabled = true;
        if (btnSvg) btnSvg.style.display = 'none';
        submitBtn.childNodes[0].textContent = 'Sending… ';
        if (errorsEl) { errorsEl.style.display = 'none'; errorsEl.innerHTML = ''; }

        try {
          const formData = new FormData(form);
          // input.files is read-only; drag-dropped files must be appended manually
          const fileInput = form.querySelector('.pf-keychain-file-input');
          if (fileInput && fileInput._selectedFile && !fileInput.files.length) {
            formData.set('fi-file-image', fileInput._selectedFile, fileInput._selectedFile.name);
          }

          const res  = await fetch('https://forminit.com/f/' + formId, {
            method: 'POST',
            body: formData,
            headers: { 'Accept': 'application/json' }
          });
          const data = await res.json();
          if (data.success) {
            form.style.display = 'none';
            if (successEl) successEl.style.display = 'block';
          } else {
            throw new Error(data.message || 'Submission failed');
          }
        } catch (err) {
          if (errorsEl) {
            errorsEl.innerHTML = '<li>' + (err.message || 'Something went wrong — please try again or email us directly.') + '</li>';
            errorsEl.style.display = 'block';
          }
        } finally {
          submitBtn.disabled = false;
          submitBtn.childNodes[0].textContent = btnLabel + ' ';
          if (btnSvg) btnSvg.style.display = '';
        }
      });
    });
  }

  /* ── Contact Form: Forminit direct API submission ───────── */
  function initContactForm() {
    document.querySelectorAll('.pf-contact-forminit').forEach((form) => {
      const formId    = form.dataset.formId;
      const sectionId = form.dataset.section;
      const successEl = document.getElementById('pf-contact-success-' + sectionId);
      const errorsEl  = document.getElementById('pf-contact-errors-' + sectionId);
      const submitBtn = form.querySelector('.pf-contact__submit');
      const btnLabel  = submitBtn ? (submitBtn.dataset.label || 'Send Message') : 'Send Message';
      const btnSvg    = submitBtn ? submitBtn.querySelector('svg') : null;

      // Auto-format phone as XXX-XXX-XXXX while typing
      const phoneInput = form.querySelector('.pf-contact__phone');
      if (phoneInput) {
        phoneInput.addEventListener('input', (e) => {
          const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
          if (digits.length <= 3) {
            e.target.value = digits;
          } else if (digits.length <= 6) {
            e.target.value = digits.slice(0, 3) + '-' + digits.slice(3);
          } else {
            e.target.value = digits.slice(0, 3) + '-' + digits.slice(3, 6) + '-' + digits.slice(6);
          }
        });
      }

      // Show chosen filename next to the file input
      form.querySelectorAll('.pf-contact__file-input').forEach((fileInput) => {
        const labelTextEl = document.getElementById(fileInput.dataset.labelId);
        if (!labelTextEl) return;
        fileInput.addEventListener('change', () => {
          labelTextEl.textContent = fileInput.files[0] ? fileInput.files[0].name : '';
        });
      });

      if (!formId) return;

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!form.checkValidity()) { form.reportValidity(); return; }

        submitBtn.disabled = true;
        if (btnSvg) btnSvg.style.display = 'none';
        submitBtn.childNodes[0].textContent = 'Sending… ';
        if (errorsEl) { errorsEl.style.display = 'none'; errorsEl.innerHTML = ''; }

        try {
          const res  = await fetch('https://forminit.com/f/' + formId, {
            method: 'POST',
            body: new FormData(form),
            headers: { 'Accept': 'application/json' }
          });
          const data = await res.json();
          if (data.success) {
            form.style.display = 'none';
            if (successEl) successEl.style.display = 'block';
          } else {
            throw new Error(data.message || 'Submission failed');
          }
        } catch (err) {
          if (errorsEl) {
            errorsEl.innerHTML = '<li>' + (err.message || 'Something went wrong — please try again or email us directly.') + '</li>';
            errorsEl.style.display = 'block';
          }
        } finally {
          submitBtn.disabled = false;
          submitBtn.childNodes[0].textContent = btnLabel + ' ';
          if (btnSvg) btnSvg.style.display = '';
        }
      });
    });
  }

  /* ── Announcement bar auto-dismiss ─────────────────────── */
  function initAnnouncement() {
    const bar = document.querySelector('.announcement-bar');
    const close = document.querySelector('[data-pf-close-announcement]');
    if (!bar || !close) return;
    close.addEventListener('click', () => {
      bar.style.transition = 'opacity 0.3s ease, max-height 0.4s ease';
      bar.style.opacity = '0';
      bar.style.maxHeight = '0';
      bar.style.overflow = 'hidden';
    });
  }

  /* ── Init ───────────────────────────────────────────────── */
  function init() {
    document.documentElement.classList.add('pf-ready');
    initReveal();
    initDropTimers();
    initCardGlow();
    initKeychainForms();
    initContactForm();
    initAccordions();
    initAnnouncement();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after Shopify section editor refreshes
  document.addEventListener('shopify:section:load', init);
})();
